import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [restaurant, setRestaurant] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [support, setSupport] = useState(null);
  const { applyServerLang } = useI18n();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setRestaurant(data.restaurant);
      setIsSuperAdmin(!!data.is_super_admin);
      setSupport(data.support || null);
      if (data.user?.language) applyServerLang(data.user.language);
      if (data.restaurant?.primary_color) {
        import("@/lib/color").then((m) => m.applyBrandColor(data.restaurant.primary_color));
      }
    } catch {
      setUser(false);
      setRestaurant(null);
      setIsSuperAdmin(false);
      setSupport(null);
    }
  }, [applyServerLang]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) localStorage.setItem("gp_token", data.token);
    await refresh();
    return data;
  }, [refresh]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.token) localStorage.setItem("gp_token", data.token);
    await refresh();
    return data;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gp_token");
    localStorage.removeItem("gp_support_session");
    setUser(false);
    setRestaurant(null);
    setIsSuperAdmin(false);
    setSupport(null);
  }, []);

  // Sprint 24 — support mode helpers
  const startSupport = useCallback(async (restaurantId) => {
    const { data } = await api.post("/admin/support-mode/start", { restaurant_id: restaurantId });
    if (data?.id) localStorage.setItem("gp_support_session", data.id);
    await refresh();
    return data;
  }, [refresh]);

  const stopSupport = useCallback(async () => {
    try { await api.post("/admin/support-mode/stop"); } catch {}
    localStorage.removeItem("gp_support_session");
    await refresh();
  }, [refresh]);

  const enableWriteSupport = useCallback(async () => {
    const { data } = await api.post("/admin/support-mode/enable-write");
    await refresh();
    return data;
  }, [refresh]);

  return (
    <AuthContext.Provider value={{
      user, restaurant, refresh, login, register, logout, setRestaurant,
      isSuperAdmin, support, startSupport, stopSupport, enableWriteSupport,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
